import React, { Suspense } from 'react';
import { RouteObject, useRoutes, Navigate, useLocation } from 'react-router-dom';
import { RouteGuard } from './guards/RouteGuard';
import { Loading } from '../components/common/Loading';
import { publicRoutes, privateRoutes } from './config';
import { AppRouteEnum } from './routeEnums';
import { useAuth } from '../layouts/AuthContext';

type CustomRouteObject = Omit<RouteObject, 'index'> & {
  index?: false;
  children?: CustomRouteObject[];
};

// Helper function to ensure route is NonIndexRouteObject
const transformRoute = (route: RouteObject): CustomRouteObject => ({
  ...route,
  index: false,
  element: route.element && (
    <Suspense fallback={<Loading />}>
      {route.element}
    </Suspense>
  ),
  children: route.children?.map(transformRoute)
});

// Helper function to wrap route with auth guard
const wrapRouteWithGuard = (route: RouteObject, requireAuth: boolean): CustomRouteObject => ({
  ...transformRoute(route),
  element: route.element && (
    <RouteGuard requireAuth={requireAuth}>
      <Suspense fallback={<Loading />}>
        {route.element}
      </Suspense>
    </RouteGuard>
  ),
  children: route.children?.map(transformRoute)
});

// Routes configuration
const routes: RouteObject[] = [
  ...publicRoutes.map(route => wrapRouteWithGuard(route, false)),
  ...privateRoutes.map(route => wrapRouteWithGuard(route, true)),
];

export const Router = () => {
  const { isAuthenticated, isCheckingAuth } = useAuth();
  const location = useLocation();
  
  // Tạo routes dựa trên trạng thái auth
  let routes_to_use = [];
  
  // Trường hợp đặc biệt: Đang check auth và đã authenticated
  if (isCheckingAuth && isAuthenticated) {
    // Chỉ hiển thị loading, không cho phép bất kỳ route nào hoạt động
    routes_to_use = [
      {
        path: '*',
        element: <Loading />
      }
    ];
  } else {
    routes_to_use = [
      {
        path: '/',
        element: isAuthenticated ? 
          <Navigate to={AppRouteEnum.MENU} replace /> : 
          <Navigate to={AppRouteEnum.LOGIN} replace />
      },
      // Nếu đang check auth, chỉ hiển thị loading
      ...(isCheckingAuth ? [
        {
          path: '*',
          element: <Loading />
        }
      ] : [
        // Khi đã check auth xong, hiển thị routes bình thường
        ...routes,
        {
          path: '*',
          element: location.pathname === AppRouteEnum.MENU && isAuthenticated ? 
            // Hiển thị menu component khi đã ở menu và đã authenticated
            <>
              {privateRoutes.find(r => r.path === AppRouteEnum.MENU)?.element}
            </> :
            // Redirect về trang phù hợp
            (isAuthenticated ? 
              <Navigate to={AppRouteEnum.MENU} replace /> : 
              <Navigate to={AppRouteEnum.LOGIN} replace />
            )
        }
      ])
    ];
  }

  // Luôn gọi useRoutes với routes đã được tính toán, không có early return
  return useRoutes(routes_to_use);
};